import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { NodeProps, Panel } from 'reactflow'
import { useShallow } from 'zustand/react/shallow'

import { NodeTypeEnum } from '@ui/interfaces'
import { ModalContainer, PanelContentManager } from '@ui/pages/pipelinesWorkflow/components/PipelineLayoutContent'
import { useLayoutPanelManagerStore, useNodeAndEdgeStore } from '@ui/stores/pipelinesWorkflow'
import clsx from 'clsx'

import { LayoutPanel, RIGHT_PANEL_CONTENT } from './interface'

export const LayoutManager = () => {
  const [node, setNode] = useState<NodeProps | null>(null)

  const { layoutPanelConfig, layoutModalConfig } = useLayoutPanelManagerStore(
    useShallow((state) => ({
      layoutPanelConfig: state.layoutPanelConfig,
      layoutModalConfig: state.layoutModalConfig,
    }))
  )

  const { nodes, currentNodeId, setCurrentNodeId } = useNodeAndEdgeStore(
    useShallow((state) => ({
      nodes: state.nodes,
      currentNodeId: state.currentNodeId,
      setCurrentNodeId: state.setCurrentNodeId,
    }))
  )

  const currentNode = useMemo(() => {
    return nodes.find((node) => node.id === currentNodeId) || nodes.find((node) => node.type === NodeTypeEnum.FIREHOSE)
  }, [currentNodeId, nodes]) as unknown as NodeProps

  useEffect(() => {
    if (!currentNode && nodes.length > 0) {
      const firehoseNode = nodes.find((node) => node.type === NodeTypeEnum.FIREHOSE)
      if (firehoseNode) {
        setCurrentNodeId(firehoseNode.id)
      }
    }

    // when current node changes, reset the widths of the panels
    if (currentNode && currentNode.id !== node?.id) {
      setNode(currentNode)
      setLeftWidth(MIN_CONSTRAINTS_WIDTH)
      setRightWidth(MIN_CONSTRAINTS_WIDTH)
      setInnerRightWidth(MIN_CONSTRAINTS_WIDTH)
    }
  }, [currentNode, nodes, setCurrentNodeId, node])

  const MAX_CONSTRAINTS_WIDTH_LEFT = useMemo(() => {
    return (window.innerWidth / 2) * 0.75
  }, [])

  const MAX_CONSTRAINTS_WIDTH_RIGHT = useMemo(() => {
    return (window.innerWidth / 2) * 0.6
  }, [])

  const MAX_INNER_CONSTRAINTS_WIDTH = useMemo(() => {
    return window.innerHeight * 0.85
  }, [])

  const MAX_CONSTRAINTS_WIDTH_CODE = useMemo(() => {
    return window.innerWidth * 0.7
  }, [])

  const MIN_CONSTRAINTS_WIDTH = useMemo(() => {
    return (window.innerWidth / 2) * 0.5
  }, [])

  const [leftWidth, setLeftWidth] = useState(MIN_CONSTRAINTS_WIDTH)
  const [rightWidth, setRightWidth] = useState(MIN_CONSTRAINTS_WIDTH)
  const [innerRightWidth, setInnerRightWidth] = useState(MIN_CONSTRAINTS_WIDTH)

  return (
    <>
      {layoutPanelConfig[LayoutPanel.LEFT_PANEL].open && (
        <Panel position="top-left" className="m-0 h-full" data-testid="left-panel" id="left-panel">
          <div className="flex size-full flex-row">
            <div
              // 75% of the window width
              style={{ width: leftWidth, maxWidth: MAX_CONSTRAINTS_WIDTH_LEFT, minWidth: MIN_CONSTRAINTS_WIDTH }}
              className="relative flex-1 bg-white">
              <ResizeHandle
                onResize={setLeftWidth}
                initialHeight={Infinity}
                initialWidth={leftWidth}
                direction="e"
                transparent={true}
              />

              <PanelContentManager
                node={currentNode!}
                panelContent={layoutPanelConfig[LayoutPanel.LEFT_PANEL].content}
              />
            </div>
          </div>
        </Panel>
      )}

      {layoutPanelConfig[LayoutPanel.RIGHT_PANEL].open && (
        <Panel position="top-right" className="m-0 h-full" data-testid="right-panel">
          <div className="flex size-full flex-row">
            {layoutPanelConfig[LayoutPanel.INNER_RIGHT_PANEL].content && (
              <div
                style={{
                  width: innerRightWidth,
                  maxWidth: layoutPanelConfig[LayoutPanel.INNER_RIGHT_PANEL].ignoreMaxConstraints
                    ? Infinity
                    : MAX_INNER_CONSTRAINTS_WIDTH,
                  minWidth: MIN_CONSTRAINTS_WIDTH,
                }}
                className="relative bg-white">
                <ResizeHandle
                  onResize={setInnerRightWidth}
                  initialHeight={Infinity}
                  initialWidth={innerRightWidth}
                  direction="w"
                />

                <PanelContentManager
                  node={currentNode!}
                  panelContent={layoutPanelConfig[LayoutPanel.INNER_RIGHT_PANEL].content}
                />
              </div>
            )}
            <div
              style={{
                width: rightWidth,
                maxWidth:
                  layoutPanelConfig[LayoutPanel.RIGHT_PANEL].content === RIGHT_PANEL_CONTENT.TRANSFORMATION_CODE
                    ? MAX_CONSTRAINTS_WIDTH_CODE
                    : MAX_CONSTRAINTS_WIDTH_RIGHT,
                // maxWidth: layoutPanelConfig[LayoutPanel.RIGHT_PANEL].ignoreMaxConstraints
                //   ? Infinity
                //   : MAX_CONSTRAINTS_WIDTH_RIGHT,
                minWidth: MIN_CONSTRAINTS_WIDTH,
              }}
              className="relative h-full bg-white">
              <ResizeHandle
                onResize={setRightWidth}
                initialHeight={Infinity}
                initialWidth={rightWidth}
                direction="w"
                transparent={true}
              />

              <PanelContentManager
                node={currentNode!}
                panelContent={layoutPanelConfig[LayoutPanel.RIGHT_PANEL].content}
              />
            </div>
          </div>
        </Panel>
      )}

      {layoutModalConfig.open && (
        <>
          <ModalContainer modalContent={layoutModalConfig.content} />
        </>
      )}
    </>
  )
}

interface ResizeHandleProps {
  onResize: (width: number) => void
  initialWidth: number
  initialHeight: number
  direction: 'n' | 's' | 'e' | 'w'
  transparent?: boolean
}

const ResizeHandle = ({ onResize, initialWidth, initialHeight, direction, transparent }: ResizeHandleProps) => {
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [startY, setStartY] = useState(0)
  const [startWidth, setStartWidth] = useState(initialWidth)
  const [startHeight, setStartHeight] = useState(initialHeight)

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      setIsDragging(true)
      setStartX(e.clientX)
      setStartWidth(initialWidth)
      setStartY(e.clientY)
      setStartHeight(initialHeight)
    },
    [initialWidth, initialHeight]
  )

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return

      switch (direction) {
        case 'n': {
          const deltaY = e.clientY - startY
          const newHeight = Math.max(200, startHeight - deltaY) // Minimum height of 200px
          onResize(newHeight)
          break
        }
        case 's': {
          const deltaY = e.clientY - startY
          const newHeight = Math.max(200, startHeight - deltaY) // Minimum height of 200px
          onResize(newHeight)
          break
        }
        case 'e': {
          const deltaX = e.clientX - startX
          const newWidth = Math.max(200, startWidth + deltaX) // Minimum width of 200px
          onResize(newWidth)
          break
        }
        case 'w': {
          const deltaX = e.clientX - startX
          const newWidth = Math.max(200, startWidth - deltaX) // Minimum width of 200px
          onResize(newWidth)
          break
        }
        default:
          break
      }
    },
    [isDragging, startX, startWidth, onResize]
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  return (
    <div
      className={clsx(
        'absolute z-50 select-none transition-colors hover:bg-primary-500',
        isDragging ? 'bg-primary-500' : transparent ? 'bg-transparent' : 'bg-primary-500',
        {
          'top-0 h-2 w-full cursor-row-resize': direction === 'n',
          'bottom-0 h-2 w-full cursor-row-resize': direction === 's',
          'right-0 top-0 h-full w-2 cursor-col-resize': direction === 'e',
          'left-0 top-0 h-full w-2 cursor-col-resize': direction === 'w',
        }
      )}
      onMouseDown={handleMouseDown}
    />
  )
}
